import styled, { css } from 'styled-components';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level6} ${spacing.level6} 0;
    gap: ${spacing.level2};
  `}

  @media (max-width: 768px) {
    ${({ theme: { spacing } }) => css`
      padding: ${spacing.level5} ${spacing.level4} 0;
    `}
  }
`;

export const Description = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
    padding: 0 ${spacing.level4};
    line-height: 1.5;
  `}
`;

export const ToolTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};

    th,
    td {
      padding: ${spacing.level3} ${spacing.level4};
      text-align: left;
      border-bottom: 1px solid ${colors.outlineVariant};
      vertical-align: top;
    }

    th {
      font-weight: ${font.labelMedium.fontWeight};
      color: ${colors.onSurfaceVariant};
      font-size: ${font.labelSmall.fontSize};
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    th:nth-child(1),
    td:nth-child(1) {
      min-width: 260px;
    }

    th:nth-child(2),
    td:nth-child(2) {
      min-width: 140px;
    }

    th:nth-child(3),
    td:nth-child(3) {
      min-width: 120px;
    }

    th:last-child,
    td:last-child {
      width: 100%;
    }
  `}
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
    padding: ${spacing.level2} ${spacing.level4};
  `}
`;

export const MonoText = styled.span`
  font-family: monospace;

  ${({ theme: { font } }) => css`
    font-size: ${font.bodySmall.fontSize};
  `}
`;

export const StatusCell = styled.div`
  display: flex;
  align-items: center;
  transform: scale(0.75);
  transform-origin: left center;
`;

export const EmptyState = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
    padding: ${spacing.level4};
    text-align: center;
  `}
`;

export const FormBody = styled.form`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} 0;
  `}
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const StepperBar = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.level5};
  `}
`;

export const FieldLabel = styled.label`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `}
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level1};
  `}
`;

export const HelpIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  cursor: help;

  ${({ theme: { colors } }) => css`
    color: ${colors.onSurfaceVariant};

    &:hover {
      color: ${colors.onSurface};
    }
  `}
`;

const fieldStyles = css<{ $error?: boolean }>`
  ${({ theme: { colors, font, spacing, borderRadius }, $error }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-radius: ${borderRadius.small};
    border: 1px solid ${$error ? colors.error : colors.outline};
    background: transparent;
    color: ${colors.onSurface};
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${font.bodyMedium.fontFamily};
    width: 100%;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${$error ? colors.error : colors.primary};
    }
  `}
`;

export const TextInput = styled.input<{ $error?: boolean }>`
  ${fieldStyles}
`;

export const Select = styled.select<{ $error?: boolean }>`
  ${fieldStyles}
`;

export const TextArea = styled.textarea<{ $error?: boolean; $tall?: boolean }>`
  ${fieldStyles}
  min-height: ${({ $tall }) => ($tall ? '240px' : '120px')};
  font-family: monospace;
  resize: vertical;
`;

export const FormError = styled.p`
  margin: 0;

  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.error};
  `}
`;

export const FieldError = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.error};
  `}
`;

export const CertSection = styled.fieldset`
  display: flex;
  flex-direction: column;
  margin: 0;

  ${({ theme: { colors, spacing, borderRadius } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level4};
    border: 1px solid ${colors.outlineVariant};
    border-radius: ${borderRadius.small};
  `}
`;

export const SectionTitle = styled.legend`
  ${({ theme: { font, colors, spacing } }) => css`
    padding: 0 ${spacing.level2};
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelMedium.fontWeight};
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  `}
`;

export const HelpText = styled.p`
  margin: 0;

  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
    line-height: 1.4;
  `}
`;

export const FileInput = styled.input`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
    padding-top: ${spacing.level3};
  `}
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level2} ${spacing.level4};
  `}
`;

export const TestRow = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
  `}
`;

export const DocLink = styled.a`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  text-decoration: none;

  ${({ theme: { colors, font, spacing } }) => css`
    gap: ${spacing.level1};
    padding: 0 ${spacing.level4};
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.primary};

    span {
      font-size: 18px;
    }

    &:hover {
      text-decoration: underline;
    }
  `}
`;
